import { WorkflowActions } from "@prisma/client";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import classNames from "classnames";
import { createEvent } from "ics";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { RRule } from "rrule";
import { z } from "zod";

import BookingPageTagManager from "@calcom/app-store/BookingPageTagManager";
import {
  getEventLocationValue,
  getSuccessPageLocationMessage,
  guessEventLocationType,
} from "@calcom/app-store/locations";
import { getEventTypeAppData } from "@calcom/app-store/utils";
import { getEventName } from "@calcom/core/event";
import dayjs, { ConfigType } from "@calcom/dayjs";
import {
  sdkActionManager,
  useEmbedNonStylesConfig,
  useIsBackgroundTransparent,
  useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import CustomBranding from "@calcom/lib/CustomBranding";
import { formatTime } from "@calcom/lib/date-fns";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import useTheme from "@calcom/lib/hooks/useTheme";
import { getEveryFreqFor } from "@calcom/lib/recurringStrings";
import { collectPageParameters, telemetryEventTypes } from "@calcom/lib/telemetry";
import { useTelemetry } from "@calcom/lib/telemetry-client";
import { getIs24hClockFromLocalStorage, isBrowserLocale24h } from "@calcom/lib/timeFormat";
import { localStorage } from "@calcom/lib/webstorage";
import { bookingMetadataSchema } from "@calcom/prisma/zod-utils";
import { Button, HeadSeo } from "@calcom/ui";
import {
  FiX,
  FiExternalLink,
  FiChevronLeft,
  FiCheck,
  FiCalendar,
  FiChevronRight,
} from "@calcom/ui/components/icon";

import { timeZone } from "@lib/clock";
import { trpc } from "@calcom/trpc/react";

import CancelBooking from "@components/booking/CancelBooking";
import EventReservationSchema from "@components/schemas/EventReservationSchema";

const stringToBoolean = z
  .string()
  .optional()
  .transform((val) => val === "true");

const querySchema = z.object({
  allRemainingBookings: stringToBoolean,
  cancel: stringToBoolean,
  changes: stringToBoolean,
  reschedule: stringToBoolean,
  isSuccessBookingPage: stringToBoolean,
  formerTime: z.string().optional(),
  email: z.string().optional(),
  eventTypeSlug: z.string().optional(),
});

function redirectToExternalUrl(url: string) {
  window.parent.location.href = url;
}

function RedirectionToast({ url }: { url: string }) {
  const [timeRemaining, setTimeRemaining] = useState(10);
  const [isToastVisible, setIsToastVisible] = useState(true);

  const { t } = useLocale();
  const timerRef = useRef<number | null>(null);
  const router = useRouter();
  const { cancel: isCancellationMode } = querySchema.parse(router.query);
  const urlWithSuccessParamsRef = useRef<string | null>();

  if (isCancellationMode && timerRef.current) {
    setIsToastVisible(false);
  }

  useEffect(() => {
    if (!isToastVisible && timerRef.current) {
      window.clearInterval(timerRef.current);
    }
  }, [isToastVisible]);

  useEffect(() => {
    const parsedExternalUrl = new URL(url);
    const parsedSuccessUrl = new URL(document.URL);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    for (const [name, value] of parsedExternalUrl.searchParams.entries()) {
      parsedSuccessUrl.searchParams.set(name, value);
    }

    const urlWithSuccessParams =
      parsedExternalUrl.origin +
      parsedExternalUrl.pathname +
      "?" +
      parsedSuccessUrl.searchParams.toString() +
      parsedExternalUrl.hash;
    urlWithSuccessParamsRef.current = urlWithSuccessParams;

    timerRef.current = window.setInterval(() => {
      if (timeRemaining > 0) {
        setTimeRemaining((timeRemaining) => timeRemaining - 1);
      } else {
        redirectToExternalUrl(urlWithSuccessParams);
        window.clearInterval(timerRef.current as number);
      }
    }, 1000);
    return () => {
      window.clearInterval(timerRef.current as number);
    };
  }, [timeRemaining, url]);

  if (!isToastVisible) return null;

  return (
    <div className="relative z-[60] pb-2 sm:pb-5">
      <div className="mx-auto w-full sm:max-w-7xl sm:px-2 lg:px-8">
        <div className="border border-green-600 bg-green-500 p-2 sm:p-3">
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex w-0 flex-1 items-center">
              <p className="truncate font-medium text-white sm:mx-3">
                <span className="md:hidden">Redirecting to {url} ...</span>
                <span className="hidden md:inline">
                  {t("you_are_being_redirected", { url, seconds: timeRemaining })}
                </span>
              </p>
            </div>
            <div className="order-3 mt-2 w-full flex-shrink-0 sm:order-2 sm:mt-0 sm:w-auto">
              <button
                onClick={() =>
                  urlWithSuccessParamsRef.current && redirectToExternalUrl(urlWithSuccessParamsRef.current)
                }
                className="flex w-full items-center justify-center rounded-sm border border-transparent bg-white px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50">
                {t("continue")}
              </button>
            </div>
            <div className="order-2 flex-shrink-0 sm:order-3 sm:ml-2">
              <button
                type="button"
                onClick={() => setIsToastVisible(false)}
                className="-mr-1 flex rounded-md p-2 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-white">
                <FiX className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RecurringBookings({
  eventType,
  recurringBookings,
  duration,
  date,
  allRemainingBookings,
  is24h,
  isCancelled,
}: any) {
  const [moreEventsVisible, setMoreEventsVisible] = useState(false);
  const { t } = useLocale();

  const recurringBookingsSorted = recurringBookings
    ? recurringBookings.sort((a: ConfigType, b: ConfigType) => (dayjs(a).isAfter(dayjs(b)) ? 1 : -1))
    : null;

  if (!duration) return null;

  if (recurringBookingsSorted && allRemainingBookings) {
    return (
      <>
        {eventType.recurringEvent?.count && (
          <span className="font-medium">
            {getEveryFreqFor({
              t,
              recurringEvent: eventType.recurringEvent,
              recurringCount: recurringBookings?.length ?? undefined,
            })}
          </span>
        )}
        {eventType.recurringEvent?.count &&
          recurringBookingsSorted.slice(0, 4).map((dateStr: string, idx: number) => (
            <div key={idx} className={classNames("mb-2", isCancelled ? "line-through" : "")}>
              {dayjs.tz(dateStr, timeZone()).format("dddd, DD MMMM YYYY")}
              <br />
              {formatTime(dateStr, is24h ? 24 : 12, timeZone().toLowerCase())} -{" "}
              {formatTime(dayjs(dateStr).add(duration, "m"), is24h ? 24 : 12, timeZone().toLowerCase())}{" "}
              <span className="text-bookinglight">({timeZone()})</span>
            </div>
          ))}
        {recurringBookingsSorted.length > 4 && (
          <Collapsible open={moreEventsVisible} onOpenChange={() => setMoreEventsVisible(!moreEventsVisible)}>
            <CollapsibleTrigger
              type="button"
              className={classNames("flex w-full", moreEventsVisible ? "hidden" : "")}>
              + {t("plus_more", { count: recurringBookingsSorted.length - 4 })}
            </CollapsibleTrigger>
            <CollapsibleContent>
              {eventType.recurringEvent?.count &&
                recurringBookingsSorted.slice(4).map((dateStr: string, idx: number) => (
                  <div key={idx} className={classNames("mb-2", isCancelled ? "line-through" : "")}>
                    {dayjs.tz(dateStr, timeZone()).format("dddd, DD MMMM YYYY")}
                    <br />
                    {formatTime(dateStr, is24h ? 24 : 12, timeZone().toLowerCase())} -{" "}
                    {formatTime(dayjs(dateStr).add(duration, "m"), is24h ? 24 : 12, timeZone().toLowerCase())}{" "}
                    <span className="text-bookinglight">({timeZone()})</span>
                  </div>
                ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </>
    );
  }

  return (
    <div className={classNames(isCancelled ? "line-through" : "")}>
      {dayjs.tz(date, timeZone()).format("dddd, DD MMMM YYYY")}
      <br />
      {formatTime(date, is24h ? 24 : 12, timeZone().toLowerCase())} -{" "}
      {formatTime(dayjs(date).add(duration, "m"), is24h ? 24 : 12, timeZone().toLowerCase())}{" "}
      <span className="text-bookinglight">({timeZone()})</span>
    </div>
  );
}

export interface BookingSuccessProps {
  uid: string;
}

export default function BookingSuccess({ uid }: BookingSuccessProps) {
  const { t } = useLocale();
  const router = useRouter();
  const { data: session } = useSession();
  const telemetry = useTelemetry();

  const {
    allRemainingBookings,
    cancel: isCancellationModeQuery,
    changes,
    formerTime,
    email,
    eventTypeSlug,
    isSuccessBookingPage,
  } = querySchema.parse(router.query);

  const { data, isLoading, error } = trpc.viewer.bookings.getBookingSuccess.useQuery(
    { uid, email, eventTypeSlug, cancel: isCancellationModeQuery },
    { enabled: !!uid, trpc: { context: { skipBatch: true } } }
  );

  const [is24h, setIs24h] = useState(isBrowserLocale24h());
  const [date, setDate] = useState<dayjs.Dayjs | null>(null);
  const [calculatedDuration, setCalculatedDuration] = useState<number | undefined>(undefined);

  const isBackgroundTransparent = useIsBackgroundTransparent();
  const isEmbed = useIsEmbed();
  const shouldAlignCentrallyInEmbed = useEmbedNonStylesConfig("align") !== "left";
  const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;

  const themeToUse = isSuccessBookingPage && data?.profile?.theme ? data.profile.theme : "light";
  useTheme(themeToUse);

  useEffect(() => {
    if (data?.bookingInfo) {
      const startTime = dayjs.utc(data.bookingInfo.startTime);
      const endTime = dayjs.utc(data.bookingInfo.endTime);
      setDate(startTime.tz(localStorage.getItem("timeOption.preferredTimeZone") || dayjs.tz.guess()));
      setCalculatedDuration(endTime.diff(startTime, "minutes"));
      setIs24h(!!getIs24hClockFromLocalStorage());
    }
  }, [data]);

  useEffect(() => {
    if (top !== window) {
      telemetry.event(telemetryEventTypes.embedView, collectPageParameters("/booking"));
    }
  }, [telemetry]);

  useEffect(() => {
    if (!data || !date || !sdkActionManager) return;
    const { eventType, bookingInfo } = data;
    const users = eventType.users;

    sdkActionManager.fire("bookingSuccessful", {
      booking: bookingInfo,
      eventType,
      date: date.toString(),
      duration: calculatedDuration,
      organizer: {
        name: users[0]?.name || "Nameless",
        email: users[0]?.email || "Email-less",
        timeZone: users[0]?.timeZone,
      },
      confirmed: !(eventType.requiresConfirmation && bookingInfo.status !== "ACCEPTED"),
    });
  }, [data, date, calculatedDuration]);

  // --- Early Returns ---
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-gray-500">{t("loading")}</p>
      </div>
    );
  }

  if (error || !data || !date) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-500">{t("something_went_wrong")}</p>
      </div>
    );
  }

  const { eventType, bookingInfo, profile, recurringBookings, paymentStatus, dynamicEventName } = data;

  const isCancellationMode = isCancellationModeQuery;

  if ((isCancellationMode || changes) && typeof window !== "undefined") {
    window.scrollTo(0, document.body.scrollHeight);
  }

  const location: ReturnType<typeof getEventLocationValue> = bookingInfo.location
    ? bookingInfo.location
    : "Praxisadresse";
  const locationVideoCallUrl: string | undefined = bookingMetadataSchema.parse(
    bookingInfo?.metadata || {}
  )?.videoCallUrl;

  const status = bookingInfo?.status;
  const reschedule = bookingInfo.status === "ACCEPTED";
  const cancellationReason = bookingInfo.cancellationReason || bookingInfo.rejectionReason;

  const attendeeName =
    typeof bookingInfo?.attendees?.[0]?.name === "string" ? bookingInfo?.attendees?.[0]?.name : "Nameless";

  const eventNameObject = {
    attendeeName,
    eventType: eventType.title,
    eventName: (dynamicEventName as string) || eventType.eventName,
    host: profile.name || "Nameless",
    location: location,
    t,
  };

  const giphyAppData = getEventTypeAppData(eventType, "giphy");
  const giphyImage = giphyAppData?.thankYouPage;

  const eventName = getEventName(eventNameObject, true);
  const needsConfirmation = eventType.requiresConfirmation && reschedule != true;
  const isCancelled = status === "CANCELLED" || status === "REJECTED";

  function setIsCancellationMode(value: boolean) {
    if (value) router.query.cancel = "true";
    else delete router.query.cancel;
    router.replace({ pathname: router.pathname, query: { ...router.query } }, undefined, { shallow: true });
  }

  function eventLink(): string {
    const optional: { location?: string } = {};
    if (locationVideoCallUrl) optional["location"] = locationVideoCallUrl;

    const event = createEvent({
      start: [
        date!.toDate().getUTCFullYear(),
        (date!.toDate().getUTCMonth() as number) + 1,
        date!.toDate().getUTCDate(),
        date!.toDate().getUTCHours(),
        date!.toDate().getUTCMinutes(),
      ],
      startInputType: "utc",
      title: eventName,
      description: eventType.description ? eventType.description : undefined,
      duration: { minutes: calculatedDuration },
      ...optional,
    });

    if (event.error) throw event.error;
    return encodeURIComponent(event.value ? event.value : false);
  }

  function getTitle(): string {
    const titleSuffix = recurringBookings ? "_recurring" : "";
    if (isCancelled) return "";
    if (needsConfirmation) {
      if (profile.name !== null)
        return t("user_needs_to_confirm_or_reject_booking" + titleSuffix, { user: profile.name });
      return t("needs_to_be_confirmed_or_rejected" + titleSuffix);
    }
    return t("emailed_you_and_attendees" + titleSuffix);
  }

  const userIsOwner = !!(session?.user?.id && eventType.owner?.id === session.user.id);
  const coachUsername = eventType.owner?.username || "";

  const title = t(
    `booking_${needsConfirmation ? "submitted" : "confirmed"}${recurringBookings ? "_recurring" : ""}`
  );
  const customInputs = bookingInfo?.customInputs as Record<string, any>;

  const locationToDisplay = getSuccessPageLocationMessage(
    locationVideoCallUrl ? locationVideoCallUrl : location,
    t,
    bookingInfo.status
  );

  const hasSMSAttendeeAction =
    eventType.workflows.find((workflowEventType) =>
      workflowEventType.workflow.steps.find((step) => step.action === WorkflowActions.SMS_ATTENDEE)
    ) !== undefined;
  const providerName = guessEventLocationType(location)?.label;

  return (
    <>
      <div className={isEmbed ? "flex h-full w-full flex-col" : "h-screen"} data-testid="success-page">
        {!isEmbed && (
          <EventReservationSchema
            reservationId={bookingInfo.uid}
            eventName={eventName}
            startTime={new Date(bookingInfo.startTime)}
            endTime={new Date(bookingInfo.endTime)}
            organizer={bookingInfo.user}
            attendees={bookingInfo.attendees}
            location={locationToDisplay}
            description={bookingInfo.description}
            status={status}
          />
        )}

        <HeadSeo title={title} description={title} />
        <BookingPageTagManager eventType={eventType} />
        <CustomBranding lightVal={profile.brandColor} darkVal={profile.darkBrandColor} />
        <main
          className={classNames(
            shouldAlignCentrally && !isEmbed ? "mx-auto" : "",
            isEmbed ? "flex-1 overflow-hidden" : "max-w-3xl"
          )}>
          <div className={classNames("overflow-y-auto", isEmbed ? "h-full w-full" : "z-50")}>
            {isSuccessBookingPage && !isCancellationMode && eventType.successRedirectUrl ? (
              <RedirectionToast url={eventType.successRedirectUrl} />
            ) : null}
            <div
              className={classNames(
                shouldAlignCentrally ? "text-center" : "",
                isEmbed
                  ? "flex h-full w-full"
                  : "flex items-end justify-center px-4 pb-20 pt-4 sm:block sm:p-0"
              )}>
              <div
                className={classNames(
                  "transition-opacity",
                  isEmbed ? "h-full w-full" : "inset-0 my-4 sm:my-0"
                )}
                aria-hidden="true">
                <div
                  className={classNames(
                    isEmbed
                      ? "main flex h-full w-full flex-col overflow-hidden"
                      : "main inline-block transform overflow-hidden rounded-lg sm:my-8 sm:max-w-xl",
                    isBackgroundTransparent ? "" : " bg-white",
                    isEmbed
                      ? "p-6"
                      : "px-8 pb-4 pt-5 text-left align-bottom transition-all sm:w-full sm:py-8 sm:align-middle"
                  )}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="modal-headline">
                  <div
                    className={classNames(
                      "mx-auto flex items-center justify-center",
                      !giphyImage && !isCancelled && !needsConfirmation
                        ? `${isEmbed ? "h-10 w-10 md:h-16 md:w-16" : "h-12 w-12"} rounded-full bg-green-100`
                        : "",
                      !giphyImage && !isCancelled && needsConfirmation
                        ? `${isEmbed ? "h-10 w-10 md:h-16 md:w-16" : "h-12 w-12"} rounded-full bg-gray-100`
                        : "",
                      isCancelled
                        ? `${isEmbed ? "h-10 w-10 md:h-16 md:w-16" : "h-12 w-12"} rounded-full bg-red-100`
                        : ""
                    )}>
                    {giphyImage && !needsConfirmation && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={giphyImage} alt="Gif from Giphy" />
                    )}
                    {!giphyImage && !needsConfirmation && !isCancelled && (
                      <FiCheck
                        className={classNames(
                          "text-green-600",
                          isEmbed ? "h-4 w-4 md:h-6 md:w-6" : "h-5 w-5"
                        )}
                      />
                    )}
                    {needsConfirmation && !isCancelled && (
                      <FiCalendar
                        className={classNames("text-gray-900", isEmbed ? "h-4 w-4 md:h-6 md:w-6" : "h-5 w-5")}
                      />
                    )}
                    {isCancelled && (
                      <FiX
                        className={classNames("text-red-600", isEmbed ? "h-4 w-4 md:h-6 md:w-6" : "h-5 w-5")}
                      />
                    )}
                  </div>
                  <div className="mb-8 mt-6 text-center last:mb-0">
                    <h3
                      className={classNames(
                        "font-semibold leading-6 text-gray-900 dark:text-white",
                        isEmbed ? "text-lg md:text-2xl lg:text-4xl" : "text-2xl"
                      )}
                      data-testid={isCancelled ? "cancelled-headline" : ""}
                      id="modal-headline">
                      {needsConfirmation && !isCancelled
                        ? recurringBookings
                          ? t("submitted_recurring")
                          : t("submitted")
                        : isCancelled
                        ? t("event_cancelled")
                        : recurringBookings
                        ? t("meeting_is_scheduled_recurring")
                        : t("meeting_is_scheduled")}
                    </h3>
                    <div className="mt-3">
                      <p
                        className={classNames(
                          "text-gray-600 dark:text-gray-300",
                          isEmbed ? "text-base md:text-xl" : "text-base"
                        )}>
                        {getTitle()}
                      </p>
                    </div>
                    {paymentStatus && (
                      <h4>
                        {!paymentStatus.success &&
                          !paymentStatus.refunded &&
                          t("booking_with_payment_cancelled")}
                        {paymentStatus.success &&
                          !paymentStatus.refunded &&
                          t("booking_with_payment_cancelled_already_paid")}
                        {paymentStatus.refunded && t("booking_with_payment_cancelled_refunded")}
                      </h4>
                    )}

                    <div
                      className={classNames(
                        "border-bookinglightest text-bookingdark dark:border-darkgray-200 mt-8 grid grid-cols-3 border-t pt-8 text-left dark:text-gray-300",
                        isEmbed ? "text-sm md:text-lg" : "text-sm"
                      )}>
                      {(isCancelled || reschedule) && cancellationReason && (
                        <>
                          <div
                            className={classNames(
                              "font-medium",
                              isEmbed ? "text-sm md:text-base" : "text-sm"
                            )}>
                            {isCancelled ? t("reason") : t("reschedule_reason_success_page")}
                          </div>
                          <div className="col-span-2 mb-6 last:mb-0">{cancellationReason}</div>
                        </>
                      )}
                      <div
                        className={classNames("font-medium", isEmbed ? "text-sm md:text-base" : "text-sm")}>
                        {t("what")}
                      </div>
                      <div className="col-span-2 mb-6 last:mb-0">{eventName}</div>
                      <div
                        className={classNames("font-medium", isEmbed ? "text-sm md:text-base" : "text-sm")}>
                        {t("when")}
                      </div>
                      <div className="col-span-2 mb-6 last:mb-0">
                        {reschedule && !!formerTime && (
                          <p className="line-through">
                            <RecurringBookings
                              eventType={eventType}
                              duration={calculatedDuration}
                              recurringBookings={recurringBookings}
                              allRemainingBookings={allRemainingBookings}
                              date={dayjs(formerTime)}
                              is24h={is24h}
                              isCancelled={isCancelled}
                            />
                          </p>
                        )}
                        <RecurringBookings
                          eventType={eventType}
                          duration={calculatedDuration}
                          recurringBookings={recurringBookings}
                          allRemainingBookings={allRemainingBookings}
                          date={date}
                          is24h={is24h}
                          isCancelled={isCancelled}
                        />
                      </div>
                      {(bookingInfo?.user || bookingInfo?.attendees) && (
                        <>
                          <div
                            className={classNames(
                              "font-medium",
                              isEmbed ? "text-sm md:text-base" : "text-sm"
                            )}>
                            {t("who")}
                          </div>
                          <div className="col-span-2 last:mb-0">
                            <>
                              {bookingInfo?.user && (
                                <div className="mb-3">
                                  <p>{bookingInfo.user.name}</p>
                                  <p className="text-bookinglight">{bookingInfo.user.email}</p>
                                </div>
                              )}
                              {bookingInfo?.attendees.map((attendee) => (
                                <div key={attendee.name} className="mb-3 last:mb-0">
                                  {attendee.name && <p>{attendee.name}</p>}
                                  <p className="text-bookinglight">{attendee.email}</p>
                                </div>
                              ))}
                            </>
                          </div>
                        </>
                      )}
                      {locationToDisplay && (
                        <>
                          <div
                            className={classNames(
                              "mt-3 font-medium",
                              isEmbed ? "text-sm md:text-base" : "text-sm"
                            )}>
                            {t("where")}
                          </div>
                          <div className="col-span-2 mt-3">
                            {locationToDisplay.startsWith("http") ? (
                              <a
                                href={locationToDisplay}
                                target="_blank"
                                title={locationToDisplay}
                                className="flex items-center gap-2 text-gray-700 underline dark:text-gray-50"
                                rel="noreferrer">
                                {providerName || "Link"}
                                <FiExternalLink className="inline h-4 w-4 text-gray-700 dark:text-white" />
                              </a>
                            ) : (
                              locationToDisplay
                            )}
                          </div>
                        </>
                      )}
                      {bookingInfo?.description && (
                        <>
                          <div className="mt-9 font-medium">{t("additional_notes")}</div>
                          <div className="col-span-2 mb-2 mt-9">
                            <p className="break-words">{bookingInfo.description}</p>
                          </div>
                        </>
                      )}
                      {customInputs &&
                        Object.keys(customInputs).map((key) => {
                          const customInput = customInputs[key];
                          const eventTypeCustomFound = eventType.customInputs?.find((ci) => ci.label === key);
                          return (
                            <div key={key}>
                              {eventTypeCustomFound?.type === "RADIO" && (
                                <>
                                  <div
                                    className={classNames(
                                      "border-bookinglightest dark:border-darkgray-300 col-span-3 mt-8 border-t pr-3 pt-8 font-medium",
                                      isEmbed ? "text-sm md:text-base" : "text-sm"
                                    )}>
                                    {eventTypeCustomFound.label}
                                  </div>
                                  <div className="col-span-3 mb-2 mt-1">
                                    {eventTypeCustomFound.options &&
                                      eventTypeCustomFound.options.map((option) => {
                                        const selected = option.label == customInput;
                                        return (
                                          <div
                                            key={option.label}
                                            className={classNames(
                                              "flex space-x-1",
                                              !selected && "text-gray-500"
                                            )}>
                                            <p>{option.label}</p>
                                            <span>{option.label === customInput && "✅"}</span>
                                          </div>
                                        );
                                      })}
                                  </div>
                                </>
                              )}
                              {eventTypeCustomFound?.type !== "RADIO" && customInput !== "" && (
                                <>
                                  <div
                                    className={classNames(
                                      "border-bookinglightest dark:border-darkgray-300 col-span-3 mt-8 border-t pr-3 pt-8 font-medium",
                                      isEmbed ? "text-sm md:text-base" : "text-sm"
                                    )}>
                                    {key}
                                  </div>
                                  <div className="col-span-3 mb-2 mt-2">
                                    {typeof customInput === "boolean" ? (
                                      <p>{customInput ? "true" : "false"}</p>
                                    ) : (
                                      <p>{customInput}</p>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      {bookingInfo?.smsReminderNumber && hasSMSAttendeeAction && (
                        <>
                          <div
                            className={classNames(
                              "mt-9 font-medium",
                              isEmbed ? "text-sm md:text-base" : "text-sm"
                            )}>
                            {t("number_sms_notifications")}
                          </div>
                          <div className="col-span-2 mb-2 mt-9">
                            <p>{bookingInfo.smsReminderNumber}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {(!needsConfirmation || !userIsOwner) &&
                    !isCancelled &&
                    (!isCancellationMode ? (
                      <>
                        <hr className="border-bookinglightest dark:border-darkgray-300 mb-8" />
                        <div className="text-center last:pb-0">
                          <span className="text-gray-900 ltr:mr-2 rtl:ml-2 dark:text-gray-50">
                            {t("need_to_make_a_change")}
                          </span>

                          {!recurringBookings && (
                            <span className="text-bookinglight inline text-gray-700">
                              <span className="underline">
                                <Link href={`/reschedule/${bookingInfo?.uid}`} legacyBehavior>
                                  {t("reschedule")}
                                </Link>
                              </span>
                              <span className="mx-2">{t("or_lowercase")}</span>
                            </span>
                          )}

                          <button
                            data-testid="cancel"
                            className={classNames(
                              "text-bookinglight text-gray-700 underline",
                              recurringBookings && "ltr:mr-2 rtl:ml-2"
                            )}
                            onClick={() => setIsCancellationMode(true)}>
                            {t("cancel")}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <hr className="border-bookinglightest dark:border-darkgray-200" />
                        <CancelBooking
                          booking={{ uid: bookingInfo?.uid, title: bookingInfo?.title, id: bookingInfo?.id }}
                          profile={{ name: profile.name, slug: profile.slug }}
                          recurringEvent={eventType.recurringEvent}
                          team={eventType?.team?.name}
                          setIsCancellationMode={setIsCancellationMode}
                          theme={isSuccessBookingPage ? profile.theme : "light"}
                          allRemainingBookings={allRemainingBookings}
                        />
                      </>
                    ))}
                  {userIsOwner &&
                    !needsConfirmation &&
                    !isCancellationMode &&
                    !isCancelled &&
                    calculatedDuration && (
                      <>
                        <hr className="border-bookinglightest dark:border-darkgray-300 mt-8" />
                        <div className="text-bookingdark align-center flex flex-row justify-center pt-8">
                          <span className="flex self-center font-medium text-gray-700 ltr:mr-2 rtl:ml-2 dark:text-gray-50">
                            {t("add_to_calendar")}
                          </span>
                          <div className="justify-left mt-1 flex text-left sm:mt-0">
                            <Link
                              href={
                                `https://calendar.google.com/calendar/r/eventedit?dates=${date
                                  .utc()
                                  .format("YYYYMMDDTHHmmss[Z]")}/${date
                                  .add(calculatedDuration, "minute")
                                  .utc()
                                  .format("YYYYMMDDTHHmmss[Z]")}&text=${eventName}&details=${
                                  eventType.description
                                }` +
                                (typeof locationVideoCallUrl === "string"
                                  ? "&location=" + encodeURIComponent(locationVideoCallUrl)
                                  : "") +
                                (eventType.recurringEvent
                                  ? "&recur=" +
                                    encodeURIComponent(new RRule(eventType.recurringEvent).toString())
                                  : "")
                              }
                              className="h-10 w-10 rounded-sm border border-gray-200 px-3 py-2 ltr:mr-2 rtl:ml-2 dark:border-gray-700 dark:text-white">
                              <svg
                                className="-mt-1.5 inline-block h-4 w-4"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24">
                                <title>Google</title>
                                <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
                              </svg>
                            </Link>
                            <Link
                              href={
                                encodeURI(
                                  "https://outlook.live.com/calendar/0/deeplink/compose?body=" +
                                    eventType.description +
                                    "&enddt=" +
                                    date.add(calculatedDuration, "minute").utc().format() +
                                    "&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&startdt=" +
                                    date.utc().format() +
                                    "&subject=" +
                                    eventName
                                ) +
                                (locationVideoCallUrl
                                  ? "&location=" + encodeURIComponent(locationVideoCallUrl)
                                  : "")
                              }
                              className="mx-2 h-10 w-10 rounded-sm border border-gray-200 px-3 py-2 dark:border-gray-700 dark:text-white"
                              target="_blank">
                              <svg
                                className="-mt-1.5 mr-1 inline-block h-4 w-4"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24">
                                <title>Microsoft Outlook</title>
                                <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
                              </svg>
                            </Link>
                            <Link
                              href={
                                encodeURI(
                                  "https://outlook.office.com/calendar/0/deeplink/compose?body=" +
                                    eventType.description +
                                    "&enddt=" +
                                    date.add(calculatedDuration, "minute").utc().format() +
                                    "&path=%2Fcalendar%2Faction%2Fcompose&rru=addevent&startdt=" +
                                    date.utc().format() +
                                    "&subject=" +
                                    eventName
                                ) +
                                (locationVideoCallUrl
                                  ? "&location=" + encodeURIComponent(locationVideoCallUrl)
                                  : "")
                              }
                              className="mx-2 h-10 w-10 rounded-sm border border-gray-200 px-3 py-2 dark:border-gray-700 dark:text-white"
                              target="_blank">
                              <svg
                                className="-mt-1.5 mr-1 inline-block h-4 w-4"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24">
                                <title>Microsoft Office</title>
                                <path d="M21.53 4.306v15.363q0 .807-.472 1.433-.472.627-1.253.85l-6.888 1.974q-.136.037-.29.055-.156.019-.293.019-.396 0-.72-.105-.321-.106-.656-.292l-4.505-2.544q-.248-.137-.391-.366-.143-.23-.143-.515 0-.434.304-.738.304-.305.739-.305h5.831V4.964l-4.38 1.563q-.533.187-.856.658-.322.472-.322 1.03v8.078q0 .496-.248.912-.25.416-.683.651l-2.072 1.13q-.286.148-.571.148-.497 0-.844-.347-.348-.347-.348-.844V6.563q0-.62.33-1.19.328-.571.874-.881L11.07.285q.248-.136.534-.21.285-.075.57-.075.211 0 .38.031.166.031.364.093l6.888 1.899q.384.11.7.329.317.217.547.52.23.305.353.67.125.367.125.764zm-1.588 15.363V4.306q0-.273-.16-.478-.163-.204-.423-.28l-3.388-.93q-.397-.111-.794-.23-.397-.117-.794-.216v19.68l4.976-1.427q.26-.074.422-.28.161-.204.161-.477z" />
                              </svg>
                            </Link>
                            <Link
                              href={"data:text/calendar," + eventLink()}
                              className="mx-2 h-10 w-10 rounded-sm border border-gray-200 px-3 py-2 dark:border-gray-700 dark:text-white"
                              download={eventType.title + ".ics"}>
                              <svg
                                version="1.1"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 1000 1000"
                                className="-mt-1.5 mr-1 inline-block h-4 w-4">
                                <title>{t("other")}</title>
                                <path d="M971.3,154.9c0-34.7-28.2-62.9-62.9-62.9H611.7c-1.3,0-2.6,0.1-3.9,0.2V10L28.7,87.3v823.4L607.8,990v-84.6c1.3,0.1,2.6,0.2,3.9,0.2h296.7c34.7,0,62.9-28.2,62.9-62.9V154.9z M607.8,636.1h44.6v-50.6h-44.6v-21.9h44.6v-50.6h-44.6v-92h277.9v230.2c0,3.8-3.1,7-7,7H607.8V636.1z M117.9,644.7l-50.6-2.4V397.5l50.6-2.2V644.7z M288.6,607.3c17.6,0.6,37.3-2.8,49.1-7.2l9.1,48c-11,5.1-35.6,9.9-66.9,8.3c-85.4-4.3-127.5-60.7-127.5-132.6c0-86.2,57.8-136.7,133.2-140.1c30.3-1.3,53.7,4,64.3,9.2l-12.2,48.9c-12.1-4.9-28.8-9.2-49.5-8.6c-45.3,1.2-79.5,30.1-79.5,87.4C208.8,572.2,237.8,605.7,288.6,607.3z M455.5,665.2c-32.4-1.6-63.7-11.3-79.1-20.5l12.6-50.7c16.8,9.1,42.9,18.5,70.4,19.4c30.1,1,46.3-10.7,46.3-29.3c0-17.8-14-28.1-48.8-40.6c-46.9-16.4-76.8-41.7-76.8-81.5c0-46.6,39.3-84.1,106.8-87.1c33.3-1.5,58.3,4.2,76.5,11.2l-15.4,53.3c-12.1-5.3-33.5-12.8-62.3-12c-28.3,0.8-41.9,13.6-41.9,28.1c0,17.8,16.1,25.5,53.6,39c52.9,18.5,78.4,45.3,78.4,86.4C575.6,629.7,536.2,669.2,455.5,665.2z M935.3,842.7c0,14.9-12.1,27-27,27H611.7c-1.3,0-2.6-0.2-3.9-0.4V686.2h270.9c19.2,0,34.9-15.6,34.9-34.9V398.4c0-19.2-15.6-34.9-34.9-34.9h-47.1v-32.3H808v32.3h-44.8v-32.3h-22.7v32.3h-43.3v-32.3h-22.7v32.3H628v-32.3h-20.2v-203c1.31.2,2.6-0.4,3.9-0.4h296.7c14.9,0,27,12.1,27,27L935.3,842.7L935.3,842.7z" />
                              </svg>
                            </Link>
                          </div>
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="dark:border-darkgray-200 dark:bg-darkgray-100 sticky bottom-0 left-0 right-0 border-t border-gray-200 bg-white px-4 py-4">
          <div className="mx-auto flex justify-end">
            <Link
              href={
                isEmbed ? `https://app.luckybrunch.de/experts/${coachUsername}` : `/experts/${coachUsername}`
              }
              target={isEmbed ? "_blank" : "_self"}>
              <Button color="primary">{t("go_to_coach_page")}</Button>
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
}
